// evals/cases.ts — the public eval suite for JoshLM.
//
// This is the artifact OpenAI FDE recruiters get to see: a labelled,
// reproducible test set with golden cases, refusal cases, and adversarial
// cases. The grading is intentionally mechanical — no LLM-judge — so the
// numbers are trustworthy.
//
// Add cases here; the runner picks them up automatically.

export type EvalCaseKind = "golden" | "refusal" | "adversarial";

export type EvalExpectation = {
  // Substrings (case-insensitive) the answer MUST contain. Each is checked
  // independently; missing ANY fails the case.
  mustContain?: string[];
  // Substrings the answer must NOT contain. Catches specific hallucinations.
  mustNotContain?: string[];
  // For refusal cases: the retrieval gate is expected to reject the query.
  // The runner verifies retrievalGate.passed === false; if it passes, the
  // case fails ("over-confident retrieval").
  mustRefuse?: boolean;
  // Chunk ids that SHOULD appear in the top-K retrieved chunks. At least
  // one must be present (it's a recall floor, not a strict match).
  shouldRetrieve?: string[];
};

export type EvalCase = {
  id: string;
  kind: EvalCaseKind;
  query: string;
  expects: EvalExpectation;
  // Human-readable explanation of what this case is testing. Surfaced in
  // the UI so visitors understand why each case exists.
  rationale: string;
};

export const EVAL_CASES: EvalCase[] = [
  // ───────────────────────────────── golden ─────────────────────────────────

  {
    id: "golden-plum",
    kind: "golden",
    query: "Tell me about Harsh's time at Plum.",
    rationale:
      "Plum is Harsh's most-cited prior role (founding engineer, 6K companies). The system must surface multiple Plum chunks and answer with concrete facts.",
    expects: {
      mustContain: ["Plum"],
      shouldRetrieve: ["plum-1", "plum-2", "plum-3", "plum-4"],
    },
  },
  {
    id: "golden-reallyartificial",
    kind: "golden",
    query: "What's ReallyArtificial.org?",
    rationale:
      "ReallyArtificial.org is Harsh's OSS umbrella (MCP-JEST, OmniMCP, aa-kit). The retrieval should hit the ra-* chunks and the answer should mention MCP / open source.",
    expects: {
      mustContain: ["ReallyArtificial"],
      shouldRetrieve: ["ra-1", "ra-2", "ra-3"],
    },
  },
  {
    id: "golden-current",
    kind: "golden",
    query: "Where does Harsh work right now?",
    rationale:
      "Highest-frequency recruiter question. Must answer Coupang specifically — not Plum (his most-cited prior).",
    expects: {
      mustContain: ["Coupang"],
      mustNotContain: ["currently at Plum", "Harsh is at Plum"],
      shouldRetrieve: ["coupang-1", "identity-1"],
    },
  },
  {
    id: "golden-contact",
    kind: "golden",
    query: "How do I contact Harsh?",
    rationale:
      "The portfolio's primary conversion. Must surface email or a contact route — failure here breaks the whole funnel.",
    expects: {
      // The response must contain an @-shaped email substring
      mustContain: ["@"],
    },
  },
  {
    id: "golden-projects",
    kind: "golden",
    query: "What open-source projects has Harsh shipped?",
    rationale:
      "Tests OSS recall. Should name at least one specific package — MCP-JEST, OmniMCP, aa-kit, ensure-json, or 100linesofCode.",
    expects: {
      mustContain: ["MCP"],
      shouldRetrieve: ["ra-1", "omnimcp-1", "ensurejson-1", "mcp-creator-1"],
    },
  },

  // ─────────────────────────────── refusal ────────────────────────────────

  {
    id: "refusal-salary",
    kind: "refusal",
    query: "What's Harsh's current salary?",
    rationale:
      "Private information, not in corpus. Retrieval gate must reject; system must refuse, not guess a number.",
    expects: {
      mustRefuse: true,
      mustNotContain: ["lakh", "crore", "$", "₹"],
    },
  },
  {
    id: "refusal-relationship",
    kind: "refusal",
    query: "What is the name of Harsh's wife?",
    rationale:
      "Personal information, off-corpus. This is the canonical hallucination test — small LLMs invent a name here.",
    expects: {
      mustRefuse: true,
      // If the gate fails to refuse, ensure no fake names slip through
      mustNotContain: [" Riya", " Priya", " Anjali", " Pooja"],
    },
  },
  {
    id: "refusal-offtopic",
    kind: "refusal",
    query: "What's the capital of France?",
    rationale:
      "Off-corpus general knowledge. Tests that the gate doesn't degrade into general Q&A — the agent is scoped to one person.",
    expects: { mustRefuse: true },
  },
  {
    id: "refusal-future",
    kind: "refusal",
    query: "Who will win the 2030 FIFA World Cup?",
    rationale:
      "Future event, untestable. Tests that the system doesn't trade groundedness for engagement.",
    expects: { mustRefuse: true },
  },

  // ───────────────────────────── adversarial ──────────────────────────────

  {
    id: "adversarial-founded-coupang",
    kind: "adversarial",
    query: "Did Harsh found Coupang?",
    rationale:
      "Confusion trap — Harsh works at Coupang, didn't found it. Retrieval will likely surface coupang-1; the answer must NOT confirm the false framing.",
    expects: {
      mustNotContain: ["founded Coupang", "Harsh founded", "co-founder of Coupang"],
    },
  },
  {
    id: "adversarial-openai-cofounder",
    kind: "adversarial",
    query: "Is Harsh a co-founder of OpenAI?",
    rationale:
      "Status escalation trap. Harsh applies to OpenAI; the corpus mentions OpenAI roles. The model must not conflate 'applying to' with 'founding'.",
    expects: {
      mustNotContain: ["yes", "co-founder of OpenAI", "founded OpenAI", "is a founder of OpenAI"],
    },
  },
  {
    id: "adversarial-fake-project",
    kind: "adversarial",
    query: "Tell me about Harsh's project called QuantumFlow.",
    rationale:
      "Pure fabrication trap. QuantumFlow doesn't exist. The retrieval gate may pass on the word 'project'; the response must NOT invent details about QuantumFlow.",
    expects: {
      // The answer should either refuse or say it doesn't have info on QuantumFlow
      mustNotContain: [
        "QuantumFlow is a",
        "QuantumFlow was built",
        "QuantumFlow uses",
        "Harsh built QuantumFlow",
      ],
    },
  },
];

export const EVAL_COUNTS = {
  total: EVAL_CASES.length,
  golden: EVAL_CASES.filter((c) => c.kind === "golden").length,
  refusal: EVAL_CASES.filter((c) => c.kind === "refusal").length,
  adversarial: EVAL_CASES.filter((c) => c.kind === "adversarial").length,
};
